// src/components/tokenPage/financialValue/SeasonalPerformanceCard.tsx

import React, { useState, useMemo } from "react";
import { DynamicIcon } from "lucide-react/dynamic";
import type { MonthlyReturn, QuarterlyReturn, AnnualReturn, HistoricalReturnsResponse } from "@/types/tokenDashboard/financialTab";

interface SeasonalPerformanceCardProps {
  historicalReturnsData?: HistoricalReturnsResponse | undefined;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  refetch?: () => void;
}

type TimeframeType = "Monthly" | "Quarterly" | "Annual";

interface TransformedData {
  headers: string[];
  rows: Array<{
    year: number;
    values: (number | null)[];
  }>;
  average: number[];
  median: number[];
}

// Transform monthly returns to table format
const transformMonthlyData = (monthlyReturns: MonthlyReturn[] | null): TransformedData | null => {
  if (!monthlyReturns || monthlyReturns.length === 0) {
    return null;
  }

  // Group by year
  const yearGroups: Record<number, (number | null)[]> = {};
  
  monthlyReturns.forEach(item => {
    if (!yearGroups[item.year]) {
      yearGroups[item.year] = new Array(12).fill(null);
    }
    const yearArray = yearGroups[item.year];
    if (yearArray) {
      // Use the 'return' field from the API response
      const returnValue = item.return;
      yearArray[item.month - 1] = returnValue;
    }
  });

  const years = Object.keys(yearGroups).map(Number).sort((a, b) => b - a); // Sort in descending order (latest year first)
  const headers = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const rows = years.map(year => {
    const yearData = yearGroups[year];
    return {
      year,
      values: yearData || new Array(12).fill(null)
    };
  });

  // Calculate averages and medians
  const averages: number[] = [];
  const medians: number[] = [];

  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const values: number[] = [];
    years.forEach(year => {
      const yearArray = yearGroups[year];
      if (yearArray) {
        const value = yearArray[monthIndex];
        if (value !== null && value !== undefined) {
          values.push(value);
        }
      }
    });

    // Calculate average
    const average = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    averages.push(average);

    // Calculate median
    if (values.length === 0) {
      medians.push(0);
    } else {
      const sortedValues = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sortedValues.length / 2);
      const median = sortedValues.length % 2 === 0 
        ? (sortedValues[mid - 1]! + sortedValues[mid]!) / 2 
        : sortedValues[mid]!;
      medians.push(median);
    }
  }

  return { headers, rows, average: averages, median: medians };
};

// Transform quarterly returns to table format
const transformQuarterlyData = (quarterlyReturns: QuarterlyReturn[] | null): TransformedData | null => {
  if (!quarterlyReturns || quarterlyReturns.length === 0) {
    return null;
  }

  // Group by year
  const yearGroups: Record<number, (number | null)[]> = {};
  
  quarterlyReturns.forEach(item => {
    if (!yearGroups[item.year]) {
      yearGroups[item.year] = new Array(4).fill(null);
    }
    const yearArray = yearGroups[item.year];
    if (yearArray) {
      yearArray[item.quarter - 1] = item.return;
    }
  });

  const years = Object.keys(yearGroups).map(Number).sort((a, b) => b - a); // Sort in descending order (latest year first)
  const headers = ["Q1", "Q2", "Q3", "Q4"];

  const rows = years.map(year => ({
    year,
    values: yearGroups[year] || new Array(4).fill(null)
  }));

  // Calculate averages and medians
  const averages: number[] = [];
  const medians: number[] = [];

  for (let quarterIndex = 0; quarterIndex < 4; quarterIndex++) {
    const values: number[] = [];
    years.forEach(year => {
      const yearArray = yearGroups[year];
      if (yearArray) {
        const value = yearArray[quarterIndex];
        if (value !== null && value !== undefined) {
          values.push(value);
        }
      }
    });

    // Calculate average
    const average = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    averages.push(average);

    // Calculate median
    if (values.length === 0) {
      medians.push(0);
    } else {
      const sortedValues = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sortedValues.length / 2);
      const median = sortedValues.length % 2 === 0 
        ? (sortedValues[mid - 1]! + sortedValues[mid]!) / 2 
        : sortedValues[mid]!;
      medians.push(median);
    }
  }

  return { headers, rows, average: averages, median: medians };
};

// Transform annual returns to table format
const transformAnnualData = (annualReturns: AnnualReturn[] | null): TransformedData | null => {
  if (!annualReturns || annualReturns.length === 0) return null;

  const sortedReturns = annualReturns.sort((a, b) => b.year - a.year); // Sort in descending order (latest year first)
  const headers = ["Annual Return"];
  
  const rows = sortedReturns.map(item => ({
    year: item.year,
    values: [item.return]
  }));

  // Calculate averages and medians
  const values = sortedReturns.map(item => item.return);
  const average = values.reduce((a, b) => a + b, 0) / values.length;
  
  const sortedValues = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sortedValues.length / 2);
  const median = sortedValues.length % 2 === 0 
    ? (sortedValues[mid - 1]! + sortedValues[mid]!) / 2 
    : sortedValues[mid]!;

  return { 
    headers, 
    rows, 
    average: [average], 
    median: [median] 
  };
};

/**
 * SeasonalPerformanceCard Component
 * Displays historical performance data in a color-coded table format
 * Supports monthly, quarterly, and annual views
 * Fully responsive with horizontal scrolling on mobile
 */
const SeasonalPerformanceCard: React.FC<SeasonalPerformanceCardProps> = ({ 
  historicalReturnsData, 
  isLoading, 
  isError, 
  error, 
  refetch 
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeType>("Monthly");

  // Transform API data to table format
  const transformedData = useMemo(() => {
    if (!historicalReturnsData?.data) return null;

    const data = historicalReturnsData.data;
    
    let result = null;
    switch (selectedTimeframe) {
      case "Monthly":
        result = transformMonthlyData(data.monthly_returns || null);
        break;
      case "Quarterly":
        result = transformQuarterlyData(data.quarterly_returns || null);
        break;
      case "Annual":
        result = transformAnnualData(data.annual_returns || null);
        break;
      default:
        return null;
    }
    
    return result;
  }, [historicalReturnsData?.data, selectedTimeframe]);

  // Get return color class based on value
  const getReturnColorClass = (value: number | null, isSpecialRow = false) => {
    if (value === null) return "bg-slate-50/5 text-slate-400";

    if (isSpecialRow) {
      return "bg-slate-100/10 text-slate-200 font-medium";
    }

    // Positive returns with green variations
    if (value >= 50) return "bg-green-600/30 text-green-200 border border-green-600/50";
    if (value >= 30) return "bg-green-500/25 text-green-200 border border-green-500/40";
    if (value >= 20) return "bg-green-400/20 text-green-100 border border-green-400/30";
    if (value >= 10) return "bg-emerald-500/20 text-emerald-200 border border-emerald-500/30";
    if (value >= 5) return "bg-emerald-400/15 text-emerald-100 border border-emerald-400/25";
    if (value > 0) return "bg-emerald-300/10 text-emerald-100 border border-emerald-300/20";
    
    // Negative returns with red variations
    if (value > -5) return "bg-red-300/10 text-red-100 border border-red-300/20";
    if (value > -10) return "bg-red-400/15 text-red-200 border border-red-400/25";
    if (value > -20) return "bg-red-500/20 text-red-300 border border-red-500/30";
    if (value > -30) return "bg-red-600/25 text-red-300 border border-red-600/40";
    return "bg-red-700/30 text-red-200 border border-red-700/50";
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 bg-gray-800/40 rounded-xl animate-pulse">
        <div className="h-5 w-28 bg-gray-700 rounded mb-3"></div>
        <div className="h-64 bg-gray-700/30 rounded-xl"></div>
      </div>
    );
  }

  // Error state with retry functionality
  if (isError) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gray-900 rounded-xl shadow-lg border border-red-700">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <DynamicIcon name="calendar" className="text-blue-400 h-5 w-5 sm:h-6 sm:w-6" />
            <span>Seasonal Performance</span>
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="p-4 bg-red-500/10 rounded-full mb-4">
            <DynamicIcon
              name="bar-chart-2"
              className="w-16 h-16 text-red-400"
            />
          </div>
          <p className="text-slate-300 text-center mb-6 max-w-md">
            {error?.message || "Failed to load seasonal performance data"}
          </p>
          <button
            onClick={() => refetch?.()}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl text-white font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg hover:shadow-xl"
          >
            <div className="flex items-center gap-2">
              <DynamicIcon name="refresh-cw" className="w-4 h-4" />
              Try Again
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (!transformedData) {
    return (
      <div className="p-4 bg-gray-800/40 rounded-xl">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="p-4 bg-gray-600/20 rounded-full mb-4">
            <DynamicIcon
              name="bar-chart-2"
              className="w-16 h-16 text-gray-400"
            />
          </div>
          <p className="text-slate-300 text-sm">No performance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-800/40 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-blue-500/15 text-blue-400 flex items-center justify-center">
            <DynamicIcon name="calendar" className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-300">Historical Returns</div>
            <div className="text-[12px] text-slate-400">Seasonality across timeframes</div>
          </div>
        </div>
        <div className="flex bg-gray-800/50 rounded-lg p-1 border border-gray-700/40">
          {(["Monthly", "Quarterly", "Annual"] as TimeframeType[]).map((timeframe) => (
            <button
              key={timeframe}
              className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-all ${
                selectedTimeframe === timeframe
                  ? "bg-blue-500/20 text-blue-300"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30"
              }`}
              onClick={() => setSelectedTimeframe(timeframe)}
            >
              {timeframe}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl bg-transparent">
        <table className="w-full border-collapse min-w-[600px] text-sm">
          <thead>
            <tr className="bg-gray-800/30">
              <th className="py-3 px-4 text-left text-gray-300 font-normal border-b border-gray-700/30">
                Year
              </th>
              {transformedData.headers.map((header: string) => (
                <th
                  key={header}
                  className="py-3 px-3 text-center text-gray-300 font-normal border-b border-gray-700/30 min-w-[80px]"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transformedData.rows.map((row, rowIndex) => (
              <tr key={row.year} className={rowIndex % 2 === 0 ? "bg-gray-800/20" : "bg-gray-800/10"}>
                <td className="py-3 px-4 text-left font-medium text-gray-200 border-b border-gray-700/30">
                  {row.year}
                </td>
                {row.values.map((value: number | null, idx: number) => (
                  <td
                    key={`${row.year}-${idx}`}
                    className={`py-3 px-3 text-center border-b border-gray-700/30 ${getReturnColorClass(value)}`}
                  >
                    {value !== null && value !== undefined ? (
                      <span className="font-normal text-sm">
                        {value > 0 ? "+" : ""}{value.toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}

            {transformedData.average && (
              <tr className="bg-gray-700/10 border-t border-gray-600/30">
                <td className="py-3 px-4 text-left font-medium text-gray-200">
                  Average
                </td>
                {transformedData.average.map((value: number, idx: number) => (
                  <td
                    key={`avg-${idx}`}
                    className="py-3 px-3 text-center bg-gray-600/10 text-gray-200 font-medium"
                  >
                    {value !== null && value !== undefined ? (
                      <span className="text-sm">{value > 0 ? "+" : ""}{value.toFixed(2)}%</span>
                    ) : (
                      <span className="text-gray-500 text-sm">—</span>
                    )}
                  </td>
                ))}
              </tr>
            )}

            {transformedData.median && (
              <tr className="bg-gray-700/10">
                <td className="py-3 px-4 text-left font-medium text-gray-200">
                  Median
                </td>
                {transformedData.median.map((value: number, idx: number) => (
                  <td
                    key={`median-${idx}`}
                    className="py-3 px-3 text-center bg-gray-600/10 text-gray-200 font-medium"
                  >
                    {value !== null && value !== undefined ? (
                      <span className="text-sm">{value > 0 ? "+" : ""}{value.toFixed(2)}%</span>
                    ) : (
                      <span className="text-gray-500 text-sm">—</span>
                    )}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sm:hidden text-xs text-gray-400 text-center mt-3 flex items-center justify-center gap-2">
        <DynamicIcon name="move-horizontal" className="w-4 h-4" />
        Swipe to see more data
      </div>
    </div>
  );
};

export default React.memo(SeasonalPerformanceCard);
